import { PageHeader } from '@/client/components/common/PageHeader';
import { StatCard } from '@/client/components/common/StatCard';
import { ProjectDialog } from '@/client/components/projects/ProjectDialog';
import type { ProjectFormState } from '@/client/components/projects/ProjectFormFields';
import { ProjectsTable } from '@/client/components/projects/ProjectsTable';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/client/components/ui/alert-dialog';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent } from '@/client/components/ui/card';
import { Input } from '@/client/components/ui/input';
import { cn } from '@/client/lib/utils';
import { trpc } from '@/client/utils/trpc';
import type { RouterOutputs } from '@/client/utils/trpc';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CheckCircle2, FolderKanban, Layers, PauseCircle, PlayCircle, Plus } from 'lucide-react';
import { useState } from 'react';

type Project = RouterOutputs['projects']['list'][number];
type StatusFilter = 'active' | 'on_hold' | 'completed' | null;

export const Route = createFileRoute('/_app/projects/')({
  component: ProjectsPage,
});

function ProjectsPage() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const { data: me } = trpc.user.me.useQuery();
  const isAdmin = me?.role === 'ADMIN';
  const { data: projects = [], isLoading } = trpc.projects.list.useQuery();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogProject, setDialogProject] = useState<Project | null>(null);
  const [deleteProject, setDeleteProject] = useState<Project | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const createMutation = trpc.projects.create.useMutation({
    onSuccess: () => {
      utils.projects.list.invalidate();
      setDialogOpen(false);
    },
    onError: (e) => setFormError(e.message),
  });
  const updateMutation = trpc.projects.update.useMutation({
    onSuccess: () => {
      utils.projects.list.invalidate();
      setDialogOpen(false);
    },
    onError: (e) => setFormError(e.message),
  });
  const removeMutation = trpc.projects.remove.useMutation({
    onSuccess: () => {
      utils.projects.list.invalidate();
      setDeleteProject(null);
    },
  });

  const counts = {
    active: projects.filter((p) => p.status === 'active').length,
    on_hold: projects.filter((p) => p.status === 'on_hold').length,
    completed: projects.filter((p) => p.status === 'completed').length,
  };

  const filtered = projects.filter((p) => {
    if (statusFilter && p.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return p.name.toLowerCase().includes(q) || (p.client_name ?? '').toLowerCase().includes(q);
    }
    return true;
  });

  const tabs: Array<{ label: string; value: StatusFilter; count: number }> = [
    { label: 'All', value: null, count: projects.length },
    { label: 'Active', value: 'active', count: counts.active },
    { label: 'On Hold', value: 'on_hold', count: counts.on_hold },
    { label: 'Completed', value: 'completed', count: counts.completed },
  ];

  function openAdd() {
    setDialogProject(null);
    setFormError(null);
    setDialogOpen(true);
  }

  function openEdit(project: Project) {
    setDialogProject(project);
    setFormError(null);
    setDialogOpen(true);
  }

  function handleProjectSubmit(form: ProjectFormState) {
    setFormError(null);
    if (dialogProject) {
      updateMutation.mutate({
        id: dialogProject.id,
        name: form.name,
        description: form.description || undefined,
        status: form.status,
        client_name: form.client_name || undefined,
      });
    } else {
      createMutation.mutate({
        name: form.name,
        description: form.description || undefined,
        status: form.status,
        client_name: form.client_name || undefined,
      });
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Projects"
        eyebrow="Workspace"
        icon={FolderKanban}
        actions={
          isAdmin && (
            <Button size="sm" onClick={openAdd}>
              <Plus className="h-4 w-4" />
              Add project
            </Button>
          )
        }
      />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Active" value={counts.active} icon={PlayCircle} tone="good" />
        <StatCard label="On hold" value={counts.on_hold} icon={PauseCircle} tone="warn" />
        <StatCard label="Completed" value={counts.completed} icon={CheckCircle2} tone="primary" />
        <StatCard label="Total" value={projects.length} icon={Layers} />
      </div>

      <Card className="p-0">
        <div className="flex items-center gap-3 border-b px-5 py-3">
          <div className="flex gap-0.5">
            {tabs.map((tab) => (
              <button
                key={String(tab.value)}
                type="button"
                onClick={() => setStatusFilter(tab.value)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  statusFilter === tab.value
                    ? 'bg-secondary text-secondary-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {tab.label}
                <span className="ml-1.5 tabular-nums text-xs opacity-60">{tab.count}</span>
              </button>
            ))}
          </div>
          <div className="flex-1" />
          <Input
            placeholder="Find a project…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 w-52"
          />
        </div>
        <CardContent className="p-0">
          <ProjectsTable
            projects={filtered}
            isLoading={isLoading}
            isAdmin={isAdmin ?? false}
            onRowClick={(id) => navigate({ to: '/projects/$id', params: { id } })}
            onEdit={openEdit}
            onDelete={setDeleteProject}
          />
        </CardContent>
      </Card>

      <ProjectDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        project={dialogProject}
        onSubmit={handleProjectSubmit}
        isPending={dialogProject ? updateMutation.isPending : createMutation.isPending}
        error={formError}
      />

      <AlertDialog open={deleteProject !== null} onOpenChange={(o) => !o && setDeleteProject(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &quot;{deleteProject?.name}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the project and all member assignments.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={removeMutation.isPending}
              onClick={() => deleteProject && removeMutation.mutate({ id: deleteProject.id })}
            >
              {removeMutation.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
